import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, timer, throwError } from 'rxjs';
import { map, catchError, retry, retryWhen, delay, take } from 'rxjs/operators';

export interface CacheConfig {
  ttl: number; // Time to live em milissegundos
  maxSize: number; // Tamanho máximo do cache
  retryAttempts: number; // Número de tentativas de retry
  retryDelay: number; // Delay entre tentativas
}

export interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
  key: string;
}

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private cacheConfig: CacheConfig = {
    ttl: 5 * 60 * 1000, // 5 minutos
    maxSize: 100,
    retryAttempts: 3,
    retryDelay: 1000
  };

  private cacheStats = new BehaviorSubject({
    hits: 0,
    misses: 0,
    size: 0,
    hitRate: 0
  });

  constructor() {
    // Limpar cache expirado a cada minuto
    timer(60000, 60000).subscribe(() => {
      this.cleanExpiredItems();
    });
  }

  /**
   * Configura o cache com novas opções
   */
  configure(config: Partial<CacheConfig>): void {
    this.cacheConfig = { ...this.cacheConfig, ...config };
  }

  /**
   * Obtém dados do cache ou executa a função se não estiver em cache
   */
  get<T>(
    key: string,
    dataFn: () => Observable<T>,
    options?: Partial<CacheConfig>
  ): Observable<T> {
    const config = { ...this.cacheConfig, ...options };
    const cacheKey = this.generateKey(key, config);

    // Verificar se está no cache e não expirado
    if (this.hasValidCache(cacheKey)) {
      this.updateStats('hit');
      return of(this.cache.get(cacheKey)!.data);
    }

    // Executar função e cachear resultado
    return dataFn().pipe(
      map(data => {
        this.setCacheItem(cacheKey, data, config);
        this.updateStats('miss');
        return data;
      }),
      retry(config.retryAttempts),
      catchError(error => {
        this.updateStats('miss');
        return throwError(() => error);
      })
    );
  }

  /**
   * Força a atualização de um item no cache
   */
  refresh<T>(key: string, dataFn: () => Observable<T>): Observable<T> {
    const cacheKey = this.generateKey(key, this.cacheConfig);
    this.cache.delete(cacheKey);
    return this.get(key, dataFn);
  }

  /**
   * Remove um item específico do cache
   */
  remove(key: string): void {
    const cacheKey = this.generateKey(key, this.cacheConfig);
    this.cache.delete(cacheKey);
    this.updateStats('remove');
  }

  /**
   * Limpa todo o cache
   */
  clear(): void {
    this.cache.clear();
    this.updateStats('clear');
  }

  /**
   * Obtém estatísticas do cache
   */
  getStats(): Observable<any> {
    return this.cacheStats.asObservable();
  }

  /**
   * Verifica se um item está em cache e válido
   */
  private hasValidCache(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    const now = Date.now();
    const isExpired = now - item.timestamp > item.ttl;
    
    if (isExpired) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Adiciona item ao cache
   */
  private setCacheItem<T>(key: string, data: T, config: CacheConfig): void {
    // Verificar tamanho máximo do cache
    if (this.cache.size >= config.maxSize) {
      this.evictOldestItem();
    }

    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: config.ttl,
      key
    };

    this.cache.set(key, cacheItem);
  }

  /**
   * Remove o item mais antigo do cache
   */
  private evictOldestItem(): void {
    let oldestKey = '';
    let oldestTime = Date.now();

    for (const [key, item] of this.cache.entries()) {
      if (item.timestamp < oldestTime) {
        oldestTime = item.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Limpa itens expirados do cache
   */
  private cleanExpiredItems(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => this.cache.delete(key));
    this.updateStats('cleanup');
  }

  /**
   * Gera chave única para o cache
   */
  private generateKey(key: string, config: CacheConfig): string {
    return `${key}_${config.ttl}`;
  }

  /**
   * Atualiza estatísticas do cache
   */
  private updateStats(action: string): void {
    const currentStats = this.cacheStats.value;
    const total = currentStats.hits + currentStats.misses;
    
    let newStats = { ...currentStats };

    switch (action) {
      case 'hit':
        newStats.hits++;
        break;
      case 'miss':
        newStats.misses++;
        break;
      case 'remove':
      case 'clear':
      case 'cleanup':
        // Não alterar hits/misses
        break;
    }

    newStats.size = this.cache.size;
    newStats.hitRate = total > 0 ? (newStats.hits / total) * 100 : 0;

    this.cacheStats.next(newStats);
  }

  /**
   * Obtém informações sobre o cache
   */
  getCacheInfo(): any {
    return {
      size: this.cache.size,
      maxSize: this.cacheConfig.maxSize,
      ttl: this.cacheConfig.ttl,
      stats: this.cacheStats.value
    };
  }
}
